import { useEffect } from 'react';

interface MetaTag {
  property?: string;
  name?: string;
  content: string;
}

interface DocumentHeadOptions {
  title?: string;
  description?: string;
  meta?: MetaTag[];
  /** Relative path (e.g. "/about"). When provided, canonical + og:url are set to https://say-it-now.app{path}. */
  path?: string;
}

const SITE_ORIGIN = 'https://say-it-now.app';

export function useDocumentHead({ title, description, meta = [], path }: DocumentHeadOptions) {
  useEffect(() => {
    const originalTitle = document.title;
    const addedElements: HTMLElement[] = [];
    const restoreAttrs: { el: Element; attr: string; value: string | null }[] = [];

    if (title) document.title = title;

    const setMetaTag = (attr: 'property' | 'name', value: string, content: string) => {
      let element = document.querySelector(`meta[${attr}="${value}"]`) as HTMLMetaElement | null;
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attr, value);
        document.head.appendChild(element);
        addedElements.push(element);
      } else {
        restoreAttrs.push({ el: element, attr: 'content', value: element.getAttribute('content') });
      }
      element.setAttribute('content', content);
    };

    if (description) {
      setMetaTag('name', 'description', description);
      setMetaTag('property', 'og:description', description);
      setMetaTag('name', 'twitter:description', description);
    }

    if (title) {
      setMetaTag('property', 'og:title', title);
      setMetaTag('name', 'twitter:title', title);
    }

    if (path) {
      const fullUrl = `${SITE_ORIGIN}${path}`;
      // canonical link
      let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.setAttribute('rel', 'canonical');
        document.head.appendChild(canonical);
        addedElements.push(canonical);
      } else {
        restoreAttrs.push({ el: canonical, attr: 'href', value: canonical.getAttribute('href') });
      }
      canonical.setAttribute('href', fullUrl);
      setMetaTag('property', 'og:url', fullUrl);
    }

    meta.forEach((tag) => {
      if (tag.property) setMetaTag('property', tag.property, tag.content);
      else if (tag.name) setMetaTag('name', tag.name, tag.content);
    });

    return () => {
      document.title = originalTitle;
      addedElements.forEach((el) => el.remove());
      restoreAttrs.forEach(({ el, attr, value }) => {
        if (value === null) el.removeAttribute(attr);
        else el.setAttribute(attr, value);
      });
    };
  }, [title, description, meta, path]);
}
